import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "../../Context/authContext";
import Avatar from "../../Components/Avatar";
import { Typography } from "../../Components/Typography";
import {
  getAllIdeas,
  getUsersByRole,
  getAllTransactions,
} from "../../Utils/apiServices";
import {
  parseListFromApiResponse,
  valueFromSettled,
} from "../../Utils/parseApiListResponse";

const normalizeComparableId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const rowTxnEmail = (item) =>
  (item?.user?.email || item?.user_data?.customerEmail || "")
    .trim()
    .toLowerCase();

const transactionBelongsToUser = (item, user) => {
  const authId = normalizeComparableId(user?.id ?? user?.user_id);
  const authEmail = (user?.email || "").trim().toLowerCase();
  if (authId && normalizeComparableId(item?.user_id) === authId) return true;
  if (authEmail && rowTxnEmail(item) === authEmail) return true;
  return false;
};

const seesAllPlatformTransactions = (role) =>
  role === "admin" || role === "program_team";

const getDateString = () =>
  new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const statCards = [
  { key: "ideas", label: "Overall Ideas", icon: "mdi:lightbulb-on-outline" },
  { key: "mentors", label: "Overall Mentors", icon: "mdi:account-tie-outline" },
  { key: "students", label: "Overall Students", icon: "mdi:school-outline" },
  { key: "donations", label: "My Donations", icon: "mdi:hand-heart-outline" },
];

const DonorHome = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    ideas: 0,
    mentors: 0,
    students: 0,
    donations: 0,
  });

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const settled = await Promise.allSettled([
          getAllIdeas(),
          getUsersByRole("mentor"),
          getUsersByRole("student"),
          getAllTransactions({
            role: user?.role,
            userId: user?.id ?? user?.user_id,
          }),
        ]);

        if (cancelled) return;

        const ideas = parseListFromApiResponse(valueFromSettled(settled[0]));
        const mentors = parseListFromApiResponse(valueFromSettled(settled[1]));
        const students = parseListFromApiResponse(valueFromSettled(settled[2]));
        const transactions = parseListFromApiResponse(valueFromSettled(settled[3]));
        let myTransactions;
        if (seesAllPlatformTransactions(user?.role)) {
          myTransactions = transactions;
        } else {
          myTransactions = transactions.filter((item) =>
            transactionBelongsToUser(item, user)
          );
          if (
            myTransactions.length === 0 &&
            transactions.length > 0 &&
            user?.role === "donor"
          ) {
            myTransactions = transactions;
          }
        }

        setStats({
          ideas: ideas.length,
          mentors: mentors.length,
          students: students.length,
          donations: myTransactions.length,
        });
      } catch (error) {
        if (!cancelled) {
          setStats({ ideas: 0, mentors: 0, students: 0, donations: 0 });
        }
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.user_id, user?.role]);

  const displayName =
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    user?.email ||
    "Guest";

  return (
    <section className="flex flex-col w-full h-auto rounded-[40px] pt-8 pb-12 gap-8 bg-primary">
      <div className="flex w-auto h-auto px-8 items-start justify-start gap-4">
        <Avatar
          src={user?.profile_picture_url}
          firstName={user?.first_name}
          lastName={user?.last_name}
          email={user?.email}
          alt="User avatar"
          size="w-16 h-16"
          showBorder={true}
          className="border-slate-200"
        />
        <div className="flex flex-col gap-1 items-start justify-start">
          <Typography as="h1" className="text-xl font-semibold text-primary-900">
            Hello, {displayName}
          </Typography>
          <Typography as="p" className="text-sm text-tertiary-600">
            {getDateString()}
          </Typography>
        </div>
      </div>

      <div className="w-auto h-auto px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="bg-primary border border-slate-200 rounded-2xl shadow-sm p-6 flex flex-col gap-3"
          >
            <div className="h-12 w-12 rounded-full bg-tertiary flex items-center justify-center">
              <Icon icon={card.icon} className="h-6 w-6 text-gray-600" />
            </div>
            <Typography as="p" className="text-sm text-tertiary-600">
              {card.label}
            </Typography>
            <Typography as="h3" className="text-3xl font-bold text-primary-900">
              {stats[card.key]}
            </Typography>
          </div>
        ))}
      </div>
    </section>
  );
};

export default DonorHome;
